import { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { Box, Spinner } from '@chakra-ui/react';

import { useHeader } from '@app/components/common/navigation/hooks/header-service';
import { useAlert } from '@app/hooks/use-alert';
import { api } from '@app/api';
import { MasterOutletUpdate } from '@app/api/models/acb';
import { useGuide } from '@app/hooks/use-guide';
import { Paper } from '@app/components/common';
import Error404 from '@app/pages/errors/404';
import { usePermissions } from '@app/hooks/use-permissions';
import { ROLES } from '@app/navigation/types';
import { useAutocomplete } from '@app/hooks';
import { getRoleName } from '@app/utils/jwt';

import { MasterOutletFormInstance } from './types';
import OutletForm from './update-form';
import { mapUpdateInstance } from './utils/mapInstance';

const OutletUpdate = () => {
  useHeader('Обновить данные мастер ТТ', []);
  const params = useParams<{ id: string }>();

  const { state } = useLocation<{ update?: MasterOutletUpdate }>();
  const history = useHistory();
  const { success, error } = useAlert();
  const [isLoading, setIsFetching] = useState<boolean>(false);
  const getAutocomplete = useAutocomplete();

  const [instance, setInstance] = useState<
    MasterOutletFormInstance | undefined
  >(undefined);
  const [origin, setOrigin] = useState<MasterOutletFormInstance>();
  
  const getMasterOutletStatus = useGuide(
    'outlet_status',
    undefined,
  );

  const getChannels = useGuide('outlet_sub_channel', undefined);

  useEffect(() => {
    void (async () => {
      
      if(!params.id || !state.update) {
        return;
      }
      const { update } = state;
      const { data: response } = await api.acb.getMasterOutlet(Number(params.id));
      if (response.code !== 200) {
        error(response.message);
        history.goBack();
        return;
      }
      const outlet = response.data;

      let region = outlet.address
        ? await getAutocomplete('region', { search: outlet.address.region })
        : undefined;
      let city = outlet.address
        ? await getAutocomplete('city', { search: outlet.address.city })
        : undefined;

      setOrigin({ 
        ...outlet,
        address: outlet.address
          ? {
              ...outlet.address,
              region: region?.[0],
              city: city?.[0],
            }
          : undefined,
      });

      let channel, subChannel, status, routeNumber: EntityBase;

      if(update.data.status_id !== undefined) {
        const statuses = await getMasterOutletStatus();
        status = statuses.find(({ id }) => id === update.data.status_id);
      }

      if(update.data.sub_channel_id !== undefined) {
        const rawChannels = await getChannels();
        const channels = rawChannels.reduce<EntityBase[]>((acc, item, index) => {
            if (acc.find(({ name }) => name === item.channel)) {
              return acc;
            }
            return [...acc, { id: index, name: item.channel }];
          }, []);
        subChannel = rawChannels.find(({ id }) => id === update.data.sub_channel_id);
        channel = channels.find(({ name }) => name.toLowerCase() === subChannel?.channel?.toLowerCase());
      }

      if(update.data.route_number_id !== undefined) {
        const { data: response } = await api.acb.getRoute(update.data.route_number_id); 
        if (response.code === 200) {
          routeNumber = response.data;
        }
      }

      if (update.data.address) {
        region = await getAutocomplete('region', { search: update.data.address.region });
        city = await getAutocomplete('city', { search: update.data.address.city });
      }

      setInstance({ 
        ...outlet,
        name:  update.data?.name || outlet?.name,
        address: update.data.address
          ? {
              ...update.data.address,
              region: region?.[0],
              city: city?.[0],
            }
          : {
              ...outlet.address,
              region: region?.[0],
              city: city?.[0],
            },
        contact_person_1: update.data.contact_person_1 || outlet.contact_person_1, 
        contact_person_2: update.data.contact_person_2 || outlet.contact_person_2, 
        director: update.data.director || outlet.director,
        email: update.data.email || outlet.email, 
        phone: update.data.phone || outlet.phone,
        route_number: routeNumber || outlet?.route_number, 
        status: status || outlet.status,
        channel: channel || outlet.channel,
        sub_channel: subChannel || undefined,
        subnet: update.data.subnet || outlet.subnet,
        title: update.data.title || outlet.title,
        visits_day: update.data.visits_day,
        updater_user: update.user,
      });
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, state]);

  const onFormSubmit = async (instance: Partial<MasterOutletFormInstance>) => {
    const { update } = state;
    setIsFetching(true);
    try {
      const saveUpdate = update.process_type === 'channels'
        ? api.acb.saveUpdateChannels
        : api.acb.saveUpdate;
      const response = await saveUpdate(mapUpdateInstance(update.id, instance, update.process_type));
      if (response.data.code === 200) {
        // await api.acb.modifyMasterOutlet(mapInstance(instance));
        await api.socket.updateStatusACL(update.id.toString(), 'acl-update-outlet', 'completed', { masterOutletId: '' + update.outlet_id, outletName: instance.name });
        success('Мастер ТТ изменена');
        
        setIsFetching(false);
        if (update.process_type === 'channels') {
          history.goBack();
        } else {
          history.push(`/acl/outlets/${update.outlet_id}/`);
        }
      } else {
        setIsFetching(false);
        error(response.data.message);
      }
    } catch (e) {
      console.log(e);
      
      setIsFetching(false);
      error(e.message);
    }
  };

  const onFormCancel = () => {
    history.goBack();
  };

  const onFormDecline = async () => {
    const { update } = state;
    try {
      const response = await api.acb.modifyClose({ id: update.id });
      if (response.data.code === 200) {
        await api.socket.updateStatusACL(update.id.toString(), 'acl-update-outlet', 'rejected', { 
          masterOutletId: '' + update.outlet_id, 
          outletName: instance.name,
          comment: 'Запрос на изменение Мастер ТТ отклонен'
        });
        setIsFetching(false);

        success('Запрос на изменение Мастер ТТ отклонен');
        history.goBack();
      } else {
        setIsFetching(false);
        error(response.data.message);
      }
    } catch (e) {
      console.log(e);
      
      setIsFetching(false);
      error(e.message);
    }
  };

  const canEdit = usePermissions({ roles: [ROLES.ADMIN, ROLES.CHIEF, ROLES.TRADE_MARKETING] });

  if (!canEdit) {
    return <Error404 />;
  }

  return (
    <Box paddingX={{ xs: 0, sm: 2, md: 4, lg: 12, xl: 22 }}>
      {!instance ? (
        <Box
          width="100%"
          height="300px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Spinner />
        </Box>
      ) : (<Paper>
        <Box paddingX={4} paddingY={6}>
          <OutletForm
            instance={instance}
            origin={origin}
            updateDistributors={state.update.data.distributors}
            onSubmit={onFormSubmit}
            onSubmitAndSend={onFormSubmit}
            onCancel={onFormCancel}
            onDeclineForm={onFormDecline}
            submitText="Применить изменения"
            submitAndSendText="Отклонить"
          />
        </Box>  
      </Paper>)}
    </Box>
  );
};

export default OutletUpdate;
